import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../../utils/axiosConfig';
import AddressInput from '../../../../components/AddressInput';

const ORDER_TYPE_ID = 2; // Cargo
const STEPS = ['Location & Price', 'Receiver Details', 'Review & Pay'];

const CargoDelivery = () => {
  const [step, setStep]       = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [orderId, setOrderId] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);

  const [form, setForm] = useState({
    title:            '',
    description:      '',
    pickup_address:   '',
    pickup_lat:       null,
    pickup_lng:       null,
    delivery_address: '',
    delivery_lat:     null,
    delivery_lng:     null,
  });

  const [receiver, setReceiver] = useState({
    recipient_name:  '',
    contact_number:  '',
    estimated_value: '',
  });

  const navigate = useNavigate();
  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // ── Real-time price calculation ──────────────────────────────────────────────
  const calcPrice = async (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return;
    setPriceLoading(true);
    try {
      const res = await axios.post('orders/errands/calculate-price/', {
        pickup_latitude:    lat1,
        pickup_longitude:   lng1,
        delivery_latitude:  lat2,
        delivery_longitude: lng2,
        order_type: 'cargo',
      });
      setPricing(res.data);
    } catch (_) { /* non-blocking */ }
    setPriceLoading(false);
  };

  const handlePickupSelect = ({ description, lat, lng }) => {
    update('pickup_address', description);
    update('pickup_lat', lat);
    update('pickup_lng', lng);
    if (lat && lng && form.delivery_lat && form.delivery_lng) {
      calcPrice(lat, lng, form.delivery_lat, form.delivery_lng);
    }
  };

  const handleDeliverySelect = ({ description, lat, lng }) => {
    update('delivery_address', description);
    update('delivery_lat', lat);
    update('delivery_lng', lng);
    if (lat && lng && form.pickup_lat && form.pickup_lng) {
      calcPrice(form.pickup_lat, form.pickup_lng, lat, lng);
    }
  };

  // ── Step 1: Create draft ─────────────────────────────────────────────────────
  const handleCreateDraft = async (e) => {
    e.preventDefault();
    if (!form.pickup_address || !form.delivery_address) {
      setError('Both pickup and delivery addresses are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('orders/errands/draft/', {
        order_type_id:      ORDER_TYPE_ID,
        title:              form.title || 'Cargo Delivery',
        description:        form.description,
        pickup_address:     form.pickup_address,
        delivery_address:   form.delivery_address,
        pickup_latitude:    form.pickup_lat  || 0,
        pickup_longitude:   form.pickup_lng  || 0,
        delivery_latitude:  form.delivery_lat  || 0,
        delivery_longitude: form.delivery_lng  || 0,
      });
      setOrderId(res.data.order_id);
      setPricing(res.data.pricing_breakdown);
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create order.');
    }
    setLoading(false);
  };

  // ── Step 2: Receiver info ────────────────────────────────────────────────────
  const handleReceiverInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`orders/errands/${orderId}/receiver-info/`, {
        recipient_name:  receiver.recipient_name,
        contact_number:  receiver.contact_number,
        estimated_value: receiver.estimated_value ? parseFloat(receiver.estimated_value) : undefined,
      });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save receiver info.');
    }
    setLoading(false);
  };

  // ── Step 3: Confirm ──────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`orders/errands/${orderId}/confirm/`);
      navigate('/payment', { state: { order_id: orderId, amount: pricing?.total } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm order.');
    }
    setLoading(false);
  };

  return (
    <div className="page">
      <div className="card">
        <div className="steps">
          {STEPS.map((label, i) => (
            <div key={i} className={`step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              <div className="step-dot">{i < step ? '✓' : i + 1}</div>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <h2>Cargo Delivery</h2>
        {error && <div className="error-box">{error}</div>}

        {/* ── STEP 0 ── */}
        {step === 0 && (
          <form onSubmit={handleCreateDraft}>
            <label className="lbl">Cargo Title (optional)</label>
            <input value={form.title} onChange={e => update('title', e.target.value)}
              placeholder="e.g. Furniture, Equipment, Machinery" className="inp" />

            <label className="lbl">Description (optional)</label>
            <textarea value={form.description} onChange={e => update('description', e.target.value)}
              placeholder="Weight, fragility, special handling..." className="inp" rows={2} style={{resize:'vertical'}} />

            <AddressInput
              label="Pickup Address"
              placeholder="Search pickup location..."
              value={form.pickup_address}
              onSelect={handlePickupSelect}
              required
            />

            <AddressInput
              label="Delivery Address"
              placeholder="Search delivery location..."
              value={form.delivery_address}
              onSelect={handleDeliverySelect}
              required
            />

            {(pricing || priceLoading) && (
              <div className="pricing-box">
                {priceLoading ? (
                  <p style={{textAlign:'center',color:'#6b7280',fontSize:13}}>Calculating cargo price...</p>
                ) : (
                  <>
                    <div className="p-row"><span>Distance</span><span>{pricing?.distance_km?.toFixed(1)} km</span></div>
                    <div className="p-row"><span>Base Fee</span><span>KES {pricing?.base_fee}</span></div>
                    <div className="p-row"><span>Distance Fee</span><span>KES {pricing?.distance_fee}</span></div>
                    <div className="p-row total"><span>Estimated Total</span><span>KES {pricing?.total}</span></div>
                  </>
                )}
              </div>
            )}

            <button type="submit" disabled={loading} className="primary-btn">
              {loading ? 'Setting up order...' : 'Continue to Receiver Details →'}
            </button>
          </form>
        )}

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <form onSubmit={handleReceiverInfo}>
            {pricing && (
              <div className="pricing-box">
                <div className="p-row"><span>Distance</span><span>{pricing.distance_km?.toFixed(1)} km</span></div>
                <div className="p-row"><span>Base Fee</span><span>KES {pricing.base_fee}</span></div>
                <div className="p-row"><span>Distance Fee</span><span>KES {pricing.distance_fee}</span></div>
                <div className="p-row total"><span>Total</span><span>KES {pricing.total}</span></div>
              </div>
            )}
            <label className="lbl">Recipient Name *</label>
            <input value={receiver.recipient_name}
              onChange={e => setReceiver(p => ({ ...p, recipient_name: e.target.value }))}
              placeholder="Full name" required className="inp" />

            <label className="lbl">Recipient Phone *</label>
            <input type="tel" value={receiver.contact_number}
              onChange={e => setReceiver(p => ({ ...p, contact_number: e.target.value }))}
              placeholder="e.g. 0712345678" required className="inp" />

            <label className="lbl">Cargo Value in KES (optional)</label>
            <input type="number" value={receiver.estimated_value}
              onChange={e => setReceiver(p => ({ ...p, estimated_value: e.target.value }))}
              placeholder="For insurance purposes" className="inp" />

            <div className="btn-row">
              <button type="button" onClick={() => setStep(0)} className="back-btn">← Back</button>
              <button type="submit" disabled={loading} className="primary-btn">
                {loading ? 'Saving...' : 'Continue →'}
              </button>
            </div>
          </form>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div>
            <div className="review-box">
              <h3>Order Summary</h3>
              <div className="r-row"><span>Type</span><span>Cargo Delivery</span></div>
              <div className="r-row"><span>Pickup</span><span>{form.pickup_address}</span></div>
              <div className="r-row"><span>Delivery</span><span>{form.delivery_address}</span></div>
              <div className="r-row"><span>Recipient</span><span>{receiver.recipient_name}</span></div>
              <div className="r-row"><span>Phone</span><span>{receiver.contact_number}</span></div>
              {receiver.estimated_value && (
                <div className="r-row"><span>Cargo Value</span><span>KES {receiver.estimated_value}</span></div>
              )}
              {pricing && <>
                <div className="r-row"><span>Distance</span><span>{pricing.distance_km?.toFixed(1)} km</span></div>
                <div className="r-row total"><span>Total Cost</span><span>KES {pricing.total}</span></div>
              </>}
            </div>
            <p className="hint">Your cargo order will be placed and a rider assigned shortly.</p>
            <div className="btn-row">
              <button type="button" onClick={() => setStep(1)} className="back-btn">← Back</button>
              <button onClick={handleConfirm} disabled={loading} className="primary-btn">
                {loading ? 'Confirming...' : 'Confirm & Proceed to Payment'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .page{min-height:100vh;background:#f3f4f6;display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;font-family:Arial,sans-serif}
        .card{background:white;border-radius:12px;padding:32px;max-width:560px;width:100%;box-shadow:0 2px 16px rgba(0,0,0,.08)}
        h2{font-size:22px;font-weight:bold;color:#111827;margin-bottom:20px}
        h3{font-size:16px;font-weight:600;color:#111827;margin-bottom:12px}
        .steps{display:flex;gap:8px;margin-bottom:24px}
        .step{display:flex;align-items:center;gap:6px;font-size:13px;color:#9ca3af;flex:1}
        .step.active{color:#3b82f6;font-weight:600}.step.done{color:#10b981}
        .step-dot{width:24px;height:24px;border-radius:50%;border:2px solid #d1d5db;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold}
        .step.active .step-dot{border-color:#3b82f6;color:#3b82f6}.step.done .step-dot{border-color:#10b981;background:#10b981;color:white}
        .error-box{background:#fee2e2;border:1px solid #f87171;color:#b91c1c;padding:10px 14px;border-radius:6px;margin-bottom:16px;font-size:14px}
        .lbl{display:block;font-size:13px;font-weight:500;color:#374151;margin-bottom:4px}
        .inp{width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:6px;font-size:15px;background:white;box-sizing:border-box;margin-bottom:14px}
        .inp:focus{outline:none;border-color:#3b82f6}
        .pricing-box{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:14px 16px;margin-bottom:20px}
        .p-row{display:flex;justify-content:space-between;font-size:14px;color:#374151;margin-bottom:6px}
        .p-row.total{font-weight:bold;font-size:16px;color:#111827;border-top:1px solid #bbf7d0;padding-top:8px;margin-top:4px}
        .review-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin-bottom:16px}
        .r-row{display:flex;justify-content:space-between;font-size:14px;color:#374151;margin-bottom:8px;gap:12px}
        .r-row span:last-child{text-align:right;color:#111827;font-weight:500}
        .r-row.total{font-weight:bold;font-size:15px;border-top:1px solid #e5e7eb;padding-top:10px;margin-top:4px}
        .hint{font-size:13px;color:#6b7280;margin-bottom:16px}
        .btn-row{display:flex;gap:12px}
        .back-btn{flex:0;padding:12px 20px;border:1px solid #d1d5db;border-radius:50px;background:white;color:#374151;font-size:15px;cursor:pointer}
        .primary-btn{flex:1;padding:12px;background:#3b82f6;color:white;border:none;border-radius:50px;font-size:15px;font-weight:500;cursor:pointer;transition:background .2s}
        .primary-btn:hover{background:#2563eb}.primary-btn:disabled{background:#9ca3af;cursor:not-allowed}
      `}</style>
    </div>
  );
};

export default CargoDelivery;
